/**
 * "Report a problem" dialog for a catalog section (MCP-5). Kit-only:
 * createModal + createButton + createLabeledInput; showToast on success;
 * reportError on failure. Send is disabled while invalid or in flight (pending
 * state + in-flight guard). A visitor who is not a real signed-in account
 * sees an inline message and no request is made.
 *
 * `submit` and `signedIn` are injectable for tests.
 */

import { createModal, createButton, createLabeledInput } from './components.js';
import { showToast } from './toast.js';
import { reportError } from '../core/report-error.js';
import { reportSection, uploadSectionReportScreenshot, isRealUser } from '../templates/auth-api.js';
import { createReportScreenshotControl } from './report-screenshot-control.js';
import { templateSiteNameFromProvenancePath } from '../shared/section-identity.js';
import {
  REPORT_KINDS,
  REPORT_KIND_LABELS,
  MAX_REPORT_MESSAGE
} from '../shared/section-report-kinds.js';

/**
 * The PP-0 context line: what this dialog is acting on. The section's label,
 * plus the template it came from when its `provenancePath` names one.
 *
 * A section with no resolvable origin gets the label alone rather than a
 * guess — provenance that is unknown stays unknown (the standing rule), and
 * an empty context is omitted entirely so the kit renders no empty wrapper.
 */
function describeReportSubject({ catalogKey, description, provenance }) {
  const label = String(description || catalogKey || '').trim();
  const site = templateSiteNameFromProvenancePath(provenance);
  if (label && site) return `${label} — from ${site}`;
  return label || site || '';
}

/**
 * shell.js owns the current-user cache; importing it statically here would
 * pull the whole shell graph into a modal that opens once in a blue moon
 * (and risk a cycle — shell reaches section-code-modal). Dynamic on demand.
 */
async function defaultSignedIn() {
  const { getCurrentUser } = await import('./shell.js');
  return isRealUser(getCurrentUser());
}

export function openSectionReportDialog({
  catalogKey,
  description = '',
  provenance = '',
  submit = (payload) => reportSection(payload),
  capture,
  uploadScreenshot = (id, blob) => uploadSectionReportScreenshot(id, blob),
  signedIn = defaultSignedIn
} = {}) {
  if (!catalogKey) return null;
  return openWhenAuthKnown({
    catalogKey,
    description,
    provenance,
    submit,
    capture,
    uploadScreenshot,
    signedIn
  });
}

function openWhenAuthKnown({
  catalogKey,
  description,
  provenance,
  submit,
  capture,
  uploadScreenshot,
  signedIn
}) {
  const kit = createModal({
    title: 'Report a problem',
    // The subject moves OUT of the title and into the kit's context line, which
    // is what PP-0 built it for and which is the only place the source template
    // fits without the title growing unbounded.
    context: describeReportSubject({ catalogKey, description, provenance }),
    size: 'sm',
    className: 'pb-secreport-modal',
    testid: 'section-report-modal',
    withFooter: true
  });

  // Auth is resolved asynchronously; the form is built only for a real account.
  const pending = document.createElement('p');
  pending.className = 'pb-set-note';
  pending.textContent = 'Checking your account…';
  kit.body.appendChild(pending);
  Promise.resolve()
    .then(() => signedIn())
    .then((ok) => {
      pending.remove();
      if (ok) buildForm();
      else {
        const msg = document.createElement('p');
        msg.className = 'pb-set-note';
        msg.textContent =
          'Sign in to report a section. Reports are tied to your account so we can follow up.';
        kit.body.appendChild(msg);
        kit.footer.appendChild(
          createButton({ label: 'Close', variant: 'ghost', onClick: () => kit.close() })
        );
      }
    })
    .catch((err) => {
      pending.textContent = 'Could not check your account.';
      reportError('Section report', err, { user: 'silent' });
    });
  return kit;

  function buildForm() {
    const kindWrap = document.createElement('div');
    kindWrap.className = 'pb-field';
    const kindId = `pb-secreport-kind-${Math.random().toString(36).slice(2, 8)}`;
    kindWrap.innerHTML = `<label class="pb-field-label" for="${kindId}">What is wrong?</label>`;
    const kindSelect = document.createElement('select');
    kindSelect.id = kindId;
    kindSelect.className = 'pb-field-input';
    for (const kind of REPORT_KINDS) {
      const opt = document.createElement('option');
      opt.value = kind;
      opt.textContent = REPORT_KIND_LABELS[kind];
      kindSelect.appendChild(opt);
    }
    kindWrap.appendChild(kindSelect);

    const messageField = createLabeledInput({
      label: 'Tell us more',
      type: 'textarea',
      placeholder: 'What did you expect, and what happened instead?'
    });
    messageField.input.maxLength = MAX_REPORT_MESSAGE;
    messageField.input.rows = 5;
    const counter = document.createElement('div');
    counter.className = 'pb-set-row-hint pb-secreport-counter';
    const error = document.createElement('p');
    error.className = 'pb-set-inline-error';
    error.hidden = true;

    const screenshot = createReportScreenshotControl({
      testid: 'section-report-screenshot',
      capture,
      onError: (err) => reportError('Section report screenshot', err, { user: 'silent' })
    });

    kit.body.append(kindWrap, messageField.wrap, counter, screenshot.wrap, error);

    let inFlight = false;
    const send = createButton({
      label: 'Send report',
      variant: 'primary',
      busyLabel: 'Sending…',
      testid: 'section-report-send',
      onAsyncClick: async () => {
        if (inFlight || !valid()) return;
        inFlight = true;
        error.hidden = true;
        try {
          const evidence = screenshot.getEvidence();
          const r = await submit({
            catalogKey,
            kind: kindSelect.value,
            message: messageField.input.value.trim(),
            screenshot: evidence?.declaration ?? null
          });
          let screenshotFailed = false;
          if (!r?.duplicate && evidence && r?.id) {
            try {
              await uploadScreenshot(r.id, evidence.blob);
            } catch (err) {
              screenshotFailed = true;
              reportError('Section report screenshot', err, { user: 'silent' });
            }
          }
          showToast(
            r?.duplicate
              ? 'You already reported this — thanks'
              : screenshotFailed
                ? 'Report sent; the screenshot could not be attached'
                : 'Thanks — report sent',
            { type: screenshotFailed ? 'warning' : 'success' }
          );
          screenshot.dispose();
          kit.close();
        } catch (err) {
          error.textContent = err?.message || 'Could not send the report.';
          error.hidden = false;
          reportError('Section report', err, { user: 'silent' });
        } finally {
          inFlight = false;
          refresh();
        }
      }
    });
    const cancel = createButton({
      label: 'Cancel',
      variant: 'ghost',
      onClick: () => {
        screenshot.dispose();
        kit.close();
      }
    });
    kit.footer.append(cancel, send);

    function valid() {
      const n = messageField.input.value.trim().length;
      return n >= 1 && n <= MAX_REPORT_MESSAGE;
    }
    function refresh() {
      const n = messageField.input.value.length;
      counter.textContent = `${n} / ${MAX_REPORT_MESSAGE}`;
      send.disabled = inFlight || !valid();
    }
    messageField.input.addEventListener('input', refresh);
    refresh();
    messageField.input.focus();
  }
}
